/**
 * The home kit — zones A–F primitives (PPL-407, design home-kit.jsx).
 *
 * Every piece is layout-agnostic and text-agnostic: it renders what it is
 * handed and reports taps. No piece composes a name, a count or a date on its
 * own — the module components do that from the payload, and the opener and
 * prompts arrive as server text.
 *
 * A card or row is one tappable region (role=button) with buttons INSIDE it
 * (dismiss, secondary actions) that stop propagation, because a <button> may
 * not contain another <button>.
 */

import { useCallback, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { Bell, Calendar, Camera, ChevronDown, ChevronRight, ChevronUp, CornerDownLeft, Users, X, AtSign } from 'react-feather';
import type { HomeReason as HomeReasonData, ReasonTone, SetupPayload } from '../../types/home';
import '../../styles/HomeDesk.css';

const TONES: ReadonlySet<string> = new Set<ReasonTone>(['magenta', 'violet', 'gold', 'green', 'dim']);

/** A server tone we do not know renders as the quiet one, never as a class-name typo. */
export function safeTone(tone: string | null | undefined): ReasonTone {
  return tone && TONES.has(tone) ? (tone as ReasonTone) : 'dim';
}

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** Keeps a click on an inner control from also firing the surface's primary tap. */
export function stop(e: MouseEvent | KeyboardEvent) {
  e.stopPropagation();
}

// ── the primary tap ──────────────────────────────────────────────────

interface TappableProps {
  onOpen?: () => void;
  className?: string;
  label?: string;
  children: ReactNode;
}

/** A div that behaves like a button when it has somewhere to go, and like a div when it does not. */
export function HomeTappable({ onOpen, className, label, children }: TappableProps) {
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!onOpen) return;
      if (e.target !== e.currentTarget) return;
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    },
    [onOpen],
  );
  return (
    <div
      className={cx(className, onOpen && 'home-tap')}
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={onOpen ? label : undefined}
      onClick={onOpen}
      onKeyDown={onOpen ? onKeyDown : undefined}
    >
      {children}
    </div>
  );
}

// ── zone A · status pill ─────────────────────────────────────────────

export function HomePill({ label }: { label: string }) {
  return (
    <div className="home-pill">
      <span className="home-pill-orb" aria-hidden="true" />
      <span className="home-pill-label">{label}</span>
    </div>
  );
}

// ── zone B · opener ──────────────────────────────────────────────────

export interface HomeOpenerProps {
  text: string;
  /** One word or name from the payload, drawn in the accent. Must occur in `text`. */
  emphasis?: string | null;
  /** Tap prefills the composer with the payload's natural reply. Never auto-sends. */
  onTap?: () => void;
  size?: 'hero' | 'default' | 'compact';
  center?: boolean;
  dim?: boolean;
}

export function HomeOpener({ text, emphasis, onTap, size = 'default', center = false, dim = false }: HomeOpenerProps) {
  const at = emphasis ? text.indexOf(emphasis) : -1;
  const content =
    emphasis && at >= 0 ? (
      <>
        {text.slice(0, at)}
        <span className="home-opener-em">{emphasis}</span>
        {text.slice(at + emphasis.length)}
      </>
    ) : (
      text
    );
  return (
    <h1
      className={cx(
        'home-opener',
        size === 'hero' && 'home-opener--hero',
        size === 'compact' && 'home-opener--compact',
        center && 'home-opener--center',
        dim && 'home-opener--dim',
        onTap && 'home-opener--tap',
      )}
      role={onTap ? 'button' : undefined}
      tabIndex={onTap ? 0 : undefined}
      onClick={onTap}
      onKeyDown={
        onTap
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onTap();
              }
            }
          : undefined
      }
    >
      {content}
    </h1>
  );
}

export function HomeOpenerSkeleton() {
  return (
    <div className="home-opener-skeleton" aria-hidden="true">
      <div className="home-skel-line" style={{ width: '86%' }} />
      <div className="home-skel-line" style={{ width: '52%' }} />
    </div>
  );
}

// ── reason chip (life brief §1.5) ────────────────────────────────────

export function HomeReason({ tone = 'magenta', icon, children }: { tone?: ReasonTone | string; icon?: ReactNode; children: ReactNode }) {
  return (
    <span className={cx('home-reason', `home-reason--${safeTone(tone)}`)}>
      {icon}
      {children}
    </span>
  );
}

/** The payload's reason object, as a chip. Null-safe so a module can pass `module.reason` straight through. */
export function HomeReasonChip({ reason }: { reason: HomeReasonData | null | undefined }) {
  if (!reason || !reason.text) return null;
  return <HomeReason tone={reason.tone}>{reason.text}</HomeReason>;
}

// ── dismiss control (quiet ×) ────────────────────────────────────────

export function HomeDismiss({ onClick, label = 'dismiss' }: { onClick?: () => void; label?: string }) {
  return (
    <button
      type="button"
      className="home-dismiss"
      aria-label={label}
      onClick={(e) => {
        stop(e);
        onClick?.();
      }}
    >
      <X size={11} aria-hidden="true" />
    </button>
  );
}

// ── chevrons + glyph wrapper ─────────────────────────────────────────

export function HomeChevron({ dir = 'right', size = 13 }: { dir?: 'right' | 'down' | 'up'; size?: number }) {
  const Icon = dir === 'down' ? ChevronDown : dir === 'up' ? ChevronUp : ChevronRight;
  return (
    <span className="home-chevron" aria-hidden="true">
      <Icon size={size} />
    </span>
  );
}

/** The 32px tile a row's icon sits in. Feather inherits `color`, so the tone is a class. */
export function HomeGlyph({ tone = 'violet', children }: { tone?: ReasonTone; children: ReactNode }) {
  return (
    <span className={cx('home-row-glyph', `home-glyph--${tone}`)} aria-hidden="true">
      {children}
    </span>
  );
}

// ── module anatomy · card density (class 1–2) ────────────────────────
// reason chip → body → sub-line / action. One primary tap; ≤ 1 secondary.

export interface HomeCardProps {
  reason?: HomeReasonData | null;
  body: ReactNode;
  sub?: ReactNode;
  /** A trailing element — a status pill or chevron. Sits in the head when the card is not dismissable, beside the body when it is. */
  right?: ReactNode;
  /** Highlighted border (class 1 and welcome back). */
  hi?: boolean;
  /** The action row under the body — at most one primary and one secondary. */
  secondary?: ReactNode;
  children?: ReactNode;
  dismissable?: boolean;
  onDismiss?: () => void;
  onOpen?: () => void;
  label?: string;
  className?: string;
}

export function HomeCard({ reason, body, sub, right, hi = false, secondary, children, dismissable = false, onDismiss, onOpen, label, className }: HomeCardProps) {
  const canDismiss = dismissable && !!onDismiss;
  return (
    <HomeTappable onOpen={onOpen} label={label} className={cx('home-card', hi && 'home-card--hi', className)}>
      <div className="home-card-head">
        <div>
          <HomeReasonChip reason={reason} />
        </div>
        {canDismiss ? <HomeDismiss onClick={onDismiss} /> : right ? <div className="home-card-right">{right}</div> : null}
      </div>
      <div className="home-card-main">
        <div className="home-card-text">
          <div className="home-card-body">{body}</div>
          {sub ? <div className="home-card-sub">{sub}</div> : null}
        </div>
        {canDismiss && right ? <div className="home-card-right">{right}</div> : null}
      </div>
      {children}
      {secondary ? <div className="home-card-secondary">{secondary}</div> : null}
    </HomeTappable>
  );
}

// ── module anatomy · row density (class 3–4) ─────────────────────────

export interface HomeRowProps {
  reason?: HomeReasonData | null;
  body: ReactNode;
  sub?: ReactNode;
  glyph?: ReactNode;
  /** An inline control on the right (e.g. RSVP). Renders before the dismiss / chevron. */
  trailing?: ReactNode;
  dismissable?: boolean;
  onDismiss?: () => void;
  onOpen?: () => void;
  chevron?: boolean;
  label?: string;
  className?: string;
}

export function HomeRow({ reason, body, sub, glyph, trailing, dismissable = false, onDismiss, onOpen, chevron = true, label, className }: HomeRowProps) {
  const canDismiss = dismissable && !!onDismiss;
  return (
    <HomeTappable onOpen={onOpen} label={label} className={cx('home-row', className)}>
      {glyph}
      <div className="home-row-main">
        <div className="home-row-line">
          <span className="home-row-body">{body}</span>
          <HomeReasonChip reason={reason} />
        </div>
        {sub ? <div className="home-row-sub">{sub}</div> : null}
      </div>
      {trailing ? <div className="home-row-trailing">{trailing}</div> : null}
      {canDismiss ? <HomeDismiss onClick={onDismiss} /> : chevron && onOpen ? <HomeChevron size={12} /> : null}
    </HomeTappable>
  );
}

// ── secondary action (inside a card) ─────────────────────────────────

export function HomeAction({ children, primary = false, small = false, disabled = false, onClick, label }: { children: ReactNode; primary?: boolean; small?: boolean; disabled?: boolean; onClick?: () => void; label?: string }) {
  return (
    <button
      type="button"
      className={cx('home-action', primary && 'home-action--primary', small && 'home-action--small')}
      disabled={disabled}
      aria-label={label}
      onClick={(e) => {
        stop(e);
        onClick?.();
      }}
    >
      {children}
    </button>
  );
}

export function HomeActions({ children }: { children: ReactNode }) {
  return <div className="home-actions">{children}</div>;
}

// ── zone D · prompts ─────────────────────────────────────────────────

export interface HomePromptsProps {
  prompts: string[];
  boxed?: boolean;
  /** Tap sends. */
  onPick?: (text: string, index: number) => void;
  /** Hover offers "edit first", which prefills instead. */
  onEdit?: (text: string, index: number) => void;
}

/**
 * A prompt row is two sibling buttons — send (the row) and "edit first" —
 * never a control nested inside a <button>: interactive content inside a
 * button is invalid HTML and its keyboard behaviour is browser-dependent.
 */
export function HomePrompts({ prompts, boxed = false, onPick, onEdit }: HomePromptsProps) {
  if (!prompts.length) return null;
  return (
    <div className={cx('home-prompts', boxed && 'home-prompts--boxed')}>
      {prompts.map((p, i) => (
        <div key={`${i}:${p}`} className="home-prompt">
          <button type="button" className="home-prompt-send" onClick={() => onPick?.(p, i)}>
            <span className="home-prompt-text">{p}</span>
            <span className="home-prompt-glyph" aria-hidden="true">
              <CornerDownLeft size={11} />
            </span>
          </button>
          {onEdit ? (
            <button type="button" className="home-prompt-edit" onClick={() => onEdit(p, i)}>
              edit first
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

// ── zone E · more row ────────────────────────────────────────────────

export function HomeMoreRow({ more = 0, chats = 0, onClick }: { more?: number; chats?: number; onClick?: () => void }) {
  const bits: string[] = [];
  if (more > 0) bits.push(`${more} more for today`);
  if (chats > 0) bits.push(`${chats} previous ${chats === 1 ? 'chat' : 'chats'}`);
  if (!bits.length) return null;
  return (
    <button type="button" className="home-more" onClick={onClick}>
      <span>{bits.join(' · ')}</span>
      <HomeChevron dir="down" size={11} />
    </button>
  );
}

// ── undo row (a dismissed module leaves this behind briefly) ────────

export function HomeUndoRow({ text, onUndo }: { text: string; onUndo?: () => void }) {
  return (
    <div className="home-undo">
      <span className="home-undo-text">{text}</span>
      <button type="button" className="home-undo-btn" onClick={onUndo}>
        undo
      </button>
    </div>
  );
}

// ── M15 · setup chip (one at a time; the reason IS the copy) ─────────

export function setupGlyph(kind: SetupPayload['kind'], size = 14): ReactNode {
  switch (kind) {
    case 'calendar':
      return <Calendar size={size} />;
    case 'notifications':
      return <Bell size={size} />;
    case 'photo':
      return <Camera size={size} />;
    case 'handle':
      return <AtSign size={size} />;
    case 'contacts':
    default:
      return <Users size={size + 1} />;
  }
}

export interface HomeSetupChipProps {
  kind: SetupPayload['kind'];
  body: ReactNode;
  sub?: ReactNode;
  dismissable?: boolean;
  onDismiss?: () => void;
  onOpen?: () => void;
  label?: string;
}

export function HomeSetupChip({ kind, body, sub, dismissable = true, onDismiss, onOpen, label }: HomeSetupChipProps) {
  return (
    <HomeTappable onOpen={onOpen} label={label} className="home-setup">
      <span className="home-setup-glyph" aria-hidden="true">
        {setupGlyph(kind)}
      </span>
      <div className="home-setup-main">
        <div className="home-setup-body">{body}</div>
        {sub ? <div className="home-setup-sub">{sub}</div> : null}
      </div>
      {dismissable && onDismiss ? <HomeDismiss onClick={onDismiss} /> : null}
    </HomeTappable>
  );
}

// ── §9 loading · the desk skeleton (two rows, no spinner) ────────────

export function HomeDeskSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <div className="home-desk-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="home-skel-row" />
      ))}
    </div>
  );
}

// ── zone C · the desk column ─────────────────────────────────────────

export function HomeDesk({ children, stale = false, className }: { children: ReactNode; stale?: boolean; className?: string }) {
  return <div className={cx('home-desk', stale && 'home-desk--stale', className)}>{children}</div>;
}
